import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { observer } from "mobx-react";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import { StyledTableRow } from "../../../../../../styles/Shipmentstyles";
import { PackagingOpsItemRow } from "../../../../../../models/ProjectManagement/ProjectPackagingSwagpacks";

interface PackagingOpsItemsTableProps {
  rows: PackagingOpsItemRow[];
  isLoading?: boolean;
  selectedSkus: string[];
  showSelection?: boolean;
  showActions?: boolean;
  onToggleSelect: (sku: string) => void;
  onToggleSelectAll: () => void;
  onInboundRow: (row: PackagingOpsItemRow) => void;
  onAdjustRow: (row: PackagingOpsItemRow) => void;
  onEditMetaRow: (row: PackagingOpsItemRow) => void;
  onAuditRow: (row: PackagingOpsItemRow) => void;
  onProductClick: (productId: number) => void;
  canDeleteSku?: boolean;
  onDeleteRow?: (row: PackagingOpsItemRow) => void;
}

const PackagingOpsItemsTable: React.FC<PackagingOpsItemsTableProps> = ({
  rows,
  isLoading = false,
  selectedSkus,
  showSelection = true,
  showActions = true,
  onToggleSelect,
  onToggleSelectAll,
  onInboundRow,
  onAdjustRow,
  onEditMetaRow,
  onAuditRow,
  onProductClick,
  canDeleteSku = false,
  onDeleteRow,
}) => {
  const theme = useTheme();
  const [rowActionAnchor, setRowActionAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [activeRow, setActiveRow] = useState<PackagingOpsItemRow | null>(null);
  const allSelected =
    rows.length > 0 &&
    rows.every((row) => selectedSkus.includes(row.sku));
  const someSelected =
    !allSelected && rows.some((row) => selectedSkus.includes(row.sku));

  const handleOpenActions = (
    event: React.MouseEvent<HTMLElement>,
    row: PackagingOpsItemRow,
  ) => {
    setRowActionAnchor(event.currentTarget);
    setActiveRow(row);
  };

  const handleCloseActions = () => {
    setRowActionAnchor(null);
    setActiveRow(null);
  };

  const emptyColSpan =
    10 + (showSelection ? 1 : 0) + (showActions ? 1 : 0);

  const canEditMeta = (row: PackagingOpsItemRow | null) =>
    !!row && (row.contributedUnits > 0 || row.onHand != null);

  const canSoftDeleteRow = (row: PackagingOpsItemRow | null) => {
    if (!canDeleteSku || !row) return false;
    if (row.onHand == null) return false;
    return (row.onHand ?? 0) === 0 && (row.reserved ?? 0) === 0;
  };

  return (
    <TableContainer sx={{ maxHeight: window.innerHeight - 360 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor:
                theme.palette.customColors?.tableGrayHeadingColor,
            }}
          >
            {showSelection && (
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onToggleSelectAll}
                disabled={rows.length === 0}
                size="small"
              />
            </AppTableHeaderCell>
            )}
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              S.NO
            </AppTableHeaderCell>
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              PRODUCT
            </AppTableHeaderCell>
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              ITEM
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              INWARDED
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              PENDING
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              AVAILABLE
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              RESERVED
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              PACK SIZE
            </AppTableHeaderCell>
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              BOX SIZE
            </AppTableHeaderCell>
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              TAG
            </AppTableHeaderCell>
            {showActions && (
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              ACTION
            </AppTableHeaderCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!isLoading && rows.length === 0 && (
            <StyledTableRow>
              <TableCell colSpan={emptyColSpan}>
                <Box py={3} textAlign="center">
                  <CustomTypography variant="body2" color="text.secondary">
                    No items found for this order.
                  </CustomTypography>
                </Box>
              </TableCell>
            </StyledTableRow>
          )}
          {rows.map((row, index) => (
            <StyledTableRow key={row.sku}>
              {showSelection && (
              <AppTableRowCell minWidth="50px">
                <Checkbox
                  checked={selectedSkus.includes(row.sku)}
                  onChange={() => onToggleSelect(row.sku)}
                  size="small"
                />
              </AppTableRowCell>
              )}
              <AppTableRowCell minWidth="50px">
                <CustomTypography variant="body2">{index + 1}</CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell
                textAlign="left"
                align="left"
                minWidth="50px"
                onClick={() => {
                  if (row.productId != null) {
                    onProductClick(row.productId);
                  }
                }}
                sx={{
                  cursor: row.productId != null ? "pointer" : "default",
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={0.25}
                  minWidth={0}
                >
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    color={theme.palette.customColors?.blackText}
                  >
                    {row.productName || "—"}
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    Product ID: {row.productId ?? "—"}
                  </CustomTypography>
                </Box>
              </AppTableRowCell>
              <AppTableRowCell textAlign="left" align="left" minWidth="50px">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={0.25}
                  minWidth={0}
                >
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.customColors?.blackText}
                  >
                    {row.title || "—"}
                  </CustomTypography>
                  <SkuCopyRow sku={row.sku} fontWeight={600} />
                  {row.clientSku && row.clientSku !== row.sku && (
                    <CustomTypography
                      variant="caption"
                      color={theme.palette.customColors?.greyTextColor}
                    >
                      Client SKU: {row.clientSku}
                    </CustomTypography>
                  )}
                </Box>
              </AppTableRowCell>

              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  textAlign="right"
                  width="100%"
                >
                  <CustomTypography variant="body1" fontWeight={600}>
                    {row.contributedUnits}
                  </CustomTypography>
                  <CustomTypography variant="caption" color="text.secondary">
                    / {row.readyQty}
                  </CustomTypography>
                </Box>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  color={
                    row.remainingInward < 0
                      ? theme.palette.error.main
                      : theme.palette.customColors?.text
                  }
                >
                  {row.remainingInward}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  textAlign="right"
                  width="100%"
                >
                  <CustomTypography variant="body1" fontWeight={600}>
                    {row.free ?? "0"}
                  </CustomTypography>
                  <CustomTypography variant="caption" color="text.secondary">
                    / {row.onHand ?? "0"}
                  </CustomTypography>
                </Box>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <CustomTypography variant="body2">
                  {row.reserved ?? "0"}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <CustomTypography variant="body2">
                  {row.packSize ?? "—"}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px">
                <CustomTypography variant="body2">
                  {row.boxSize || "—"}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px">
                <CustomTypography variant="body2">
                  {row.tag || row.tagId || "—"}
                </CustomTypography>
              </AppTableRowCell>
              {showActions && (
              <AppTableRowCell minWidth="50px">
                <Button
                  variant="outlined"
                  onClick={(event) => handleOpenActions(event, row)}
                  sx={{
                    minWidth: "94px",
                    height: "32px",
                    fontSize: "0.8rem",
                    textTransform: "none",
                    borderColor: theme.palette.customColors?.greyBorder,
                    color: theme.palette.customColors?.text,
                  }}
                >
                  Actions
                </Button>
              </AppTableRowCell>
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
      <Menu
        anchorEl={rowActionAnchor}
        open={Boolean(rowActionAnchor)}
        onClose={handleCloseActions}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          disabled={
            !activeRow ||
            activeRow.remainingInward <= 0 ||
            activeRow.productId == null
          }
          onClick={() => {
            if (activeRow) onInboundRow(activeRow);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Inward
          </CustomTypography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activeRow) onAdjustRow(activeRow);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Adjust
          </CustomTypography>
        </MenuItem>
        <MenuItem
          disabled={!canEditMeta(activeRow)}
          onClick={() => {
            if (activeRow) onEditMetaRow(activeRow);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Edit Meta
          </CustomTypography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activeRow) onAuditRow(activeRow);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Audit
          </CustomTypography>
        </MenuItem>
        {canDeleteSku && (
          <MenuItem
            disabled={!canSoftDeleteRow(activeRow)}
            onClick={() => {
              if (activeRow && canSoftDeleteRow(activeRow)) {
                onDeleteRow?.(activeRow);
              }
              handleCloseActions();
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              Delete SKU
            </CustomTypography>
          </MenuItem>
        )}
      </Menu>
    </TableContainer>
  );
};

export default observer(PackagingOpsItemsTable);
